import React, { useState } from 'react';
import { X, Plus, Search, Layers, Navigation, Pencil, Download, Undo2 } from 'lucide-react';
import type { Field } from '../../types';
import { fieldAreaHa, getAreaTypeDefinition } from '../../lib/domain';
import { isJagtPoiField, parseJagtPoi } from '../../lib/poi';

export interface MobileFieldsSheetProps {
  fields: Field[];
  selectedFieldId: string;
  onSelectField: (fieldId: string) => void;
  onClose: () => void;
  onTriggerDraw: () => void;
  onTriggerGps: () => void;
  /** Pro/admin: åbner eksportarket (DXF/CSV). Topbaren er ryddet op og har ikke længere eksportknappen. */
  onOpenExport?: () => void;
  /** Navn på den senest slettede mark (kan gendannes). Skrivebordets "Gendan"-knap findes ikke i mobil-skallen. */
  deletedFieldName?: string;
  onRestoreDeleted?: () => void;
}

export const MobileFieldsSheet: React.FC<MobileFieldsSheetProps> = ({
  fields,
  selectedFieldId,
  onSelectField,
  onClose,
  onTriggerDraw,
  onTriggerGps,
  onOpenExport,
  deletedFieldName,
  onRestoreDeleted,
}) => {
  const [filter, setFilter] = useState('');

  const filteredFields = fields.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <>
      <div className="mobile-inspector-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="mobile-fields-sheet" role="region" aria-label="Dine marker og arealer">
        <div className="mobile-fields-list-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers size={18} color="#4ade80" />
            <strong style={{ fontSize: '0.95rem', color: '#fff' }}>
              Dine arealer ({fields.length})
            </strong>
          </div>
          <button
            type="button"
            className="mobile-sheet-close-btn"
            onClick={onClose}
            aria-label="Luk marker"
            title="Luk"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filter and Quick Draw Actions */}
        <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Søg i dine marker..."
              style={{
                width: '100%',
                minHeight: 44,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: 8,
                padding: '0 36px 0 12px',
                color: '#fff',
                fontSize: '0.85rem',
                boxSizing: 'border-box',
              }}
            />
            <Search
              size={16}
              color="#94a3b8"
              style={{ position: 'absolute', right: 12, top: 14, pointerEvents: 'none' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="mobile-sec-btn"
              onClick={() => {
                onClose();
                onTriggerDraw();
              }}
              style={{
                flex: 1,
                background: 'rgba(22, 163, 74, 0.18)',
                borderColor: 'rgba(74, 222, 128, 0.3)',
                color: '#4ade80',
              }}
            >
              <Pencil size={15} />
              <span>Tegn ny mark</span>
            </button>
            <button
              type="button"
              className="mobile-sec-btn"
              onClick={() => {
                onClose();
                onTriggerGps();
              }}
              style={{ flex: 1 }}
            >
              <Navigation size={15} color="#22c55e" />
              <span>Gå grænse</span>
            </button>
            {onOpenExport && (
              <button
                type="button"
                className="mobile-sec-btn"
                onClick={() => {
                  onClose();
                  onOpenExport();
                }}
                style={{ flex: 1 }}
              >
                <Download size={15} color="#38bdf8" />
                <span>Eksportér</span>
              </button>
            )}
          </div>
        </div>

        {deletedFieldName && onRestoreDeleted && (
          <div style={{ padding: '0 12px 8px' }}>
            <button
              type="button"
              className="mobile-sec-btn restore-field"
              onClick={() => {
                onClose();
                onRestoreDeleted();
              }}
              style={{ width: '100%' }}
            >
              <Undo2 size={15} />
              <span>Gendan {deletedFieldName}</span>
            </button>
          </div>
        )}

        <div className="mobile-fields-list-scroll field-list">
          {filteredFields.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '30px 16px',
                color: '#94a3b8',
                fontSize: '0.88rem',
              }}
            >
              {filter
                ? 'Ingen marker matcher din søgning.'
                : 'Du har ikke oprettet nogen marker endnu.'}
            </div>
          ) : (
            filteredFields.map((f, index) => {
              const isPoi = isJagtPoiField(f);
              const area = fieldAreaHa(f.geometry);
              const def = getAreaTypeDefinition(f.areaType, f.kind);

              return (
                <div
                  key={f.id}
                  className={`mobile-field-item-card field-row field-item ${f.id === selectedFieldId ? 'selected' : ''}`}
                  onClick={() => {
                    onSelectField(f.id);
                    onClose();
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${f.name}, ${isPoi ? 'punkt' : `${area.toFixed(2)} ha`}`}
                >
                  <div className="mobile-field-item-left">
                    <span className="mobile-field-num-badge field-number">{index + 1}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <strong className="mobile-field-item-title">{f.name}</strong>
                      <small style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        {isPoi ? 'Jagt-punkt' : def.label}
                      </small>
                    </div>
                  </div>

                  {!isPoi && (
                    <div className="mobile-field-item-area field-area">
                      {area.toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      ha
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};
